import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Save, Loader2, Eye, ScrollText, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import CourseCompletionCelebration from '../components/CourseCompletionCelebration';

/** AdminCourseCelebrationPage — dedicated editor for the "ختم المساق"
 *  celebration text. Lives under the "الإعدادات والنظام" section. Persists
 *  to the shared `achievement_templates` collection under the
 *  `course_completed` event, and offers a live preview of the resulting
 *  fancy Islamic-scholarly modal so admins can iterate confidently. */
const TOKENS = ['{reader_name}', '{course_title}'];

const AdminCourseCelebrationPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', is_active: true });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/achievement-templates');
      const tpl = (res.data?.templates || {}).course_completed || {};
      setForm({
        title: tpl.title || 'تهانينا! أتممت المساق بنجاح',
        message: tpl.message || (
          'بسم الله الرحمن الرحيم\n\n' +
          '{reader_name}، ما شاء الله تبارك الله!\n\n' +
          'لقد أتممت مساق «{course_title}» بحمد الله وتوفيقه.\n\n' +
          'قال الإمام أحمد بن حنبل رحمه الله:\n' +
          '«الناس إلى العلم أحوج منهم إلى الطعام والشراب»\n\n' +
          'نفع الله بك وبعلمك، وجعله في ميزان حسناتك.'
        ),
        is_active: tpl.is_active !== false,
      });
    } catch { toast.error('فشل تحميل قالب الاحتفال'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/achievement-templates/course_completed', {
        title: form.title,
        message: form.message,
        is_active: form.is_active,
      });
      toast.success('تم حفظ نص الاحتفال بنجاح');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحفظ');
    } finally { setSaving(false); }
  };

  const insertToken = (tok) => {
    // Inserts a token into the message at the cursor position (or appends).
    const ta = document.getElementById('acc-message');
    if (!ta) return;
    const start = ta.selectionStart ?? form.message.length;
    const end = ta.selectionEnd ?? form.message.length;
    const next = form.message.slice(0, start) + tok + form.message.slice(end);
    setForm((p) => ({ ...p, message: next }));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + tok.length, start + tok.length);
    }, 0);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-course-celebration">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-accent flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-amber-500" />
            نص احتفال ختم المساق
          </h1>
          <p className="text-sm text-muted-foreground mt-1">حرّر النص الذي يظهر للقارئ في المودال الفاخر عند ختمه لأي مساق.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="reload-btn">
          <RefreshCw className="w-4 h-4 ml-1" /> تحديث
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <Card className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">العنوان الرئيس</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="مثال: مبروك ختم المساق"
                data-testid="acc-title"
              />
              <p className="text-xs text-muted-foreground mt-1">يظهر بخط Amiri فاخر في قلب المودال.</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold">نص التهنئة</label>
                <div className="flex gap-1.5 flex-wrap">
                  {TOKENS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => insertToken(t)}
                      className="text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-200 transition-colors"
                      data-testid={`insert-${t.replace(/[{}]/g, '')}`}
                    >
                      إدراج {t}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                id="acc-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={14}
                className="w-full border-2 rounded-xl p-4 font-[Amiri,serif] text-[15px] leading-8 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y bg-amber-50/40"
                dir="rtl"
                data-testid="acc-message"
                placeholder="اكتب نصّ الاحتفال هنا. كل سطر جديد يظهر كسطر مستقل."
              />
              <p className="text-xs text-muted-foreground mt-1">
                يُعرض النص محافظاً على أسطره وفواصله كما تكتبها. استخدم <code>{'{reader_name}'}</code>
                لاسم القارئ و<code>{'{course_title}'}</code> لاسم المساق.
              </p>
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="acc-active"
                />
                القالب نشط (يُعرض للقرّاء)
              </label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  data-testid="acc-preview"
                >
                  <Eye className="w-4 h-4 ml-1" /> معاينة
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="acc-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Save className="w-4 h-4 ml-1" />}
                  حفظ التغييرات
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-5 bg-amber-50/40 border-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <ScrollText className="w-5 h-5 text-amber-700" />
              <h3 className="font-bold text-amber-900">نصيحة تحرير</h3>
            </div>
            <ul className="text-sm text-amber-900 space-y-1 list-disc pr-6 leading-relaxed">
              <li>ابدأ بالبسملة أو استفتاح شرعي، ثم اترك سطراً فارغاً لتحسين البصرية.</li>
              <li>اقتبس بيت شعر أو أثراً عن أحد العلماء بين علامتَي «...»؛ يظهر أنيقاً في المودال.</li>
              <li>اختم بدعاء قصير للقارئ ليخرج بشعور طيّب مبارك.</li>
            </ul>
          </Card>

          {/* Live preview using the actual celebration component */}
          {previewOpen && (
            <CourseCompletionCelebration
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              courseTitle="اسم المساق (عيّنة)"
              readerName="أخي القارئ"
              overrideTemplate={{ title: form.title, message: form.message }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminCourseCelebrationPage;
